

































































































































































import { Vue, Component } from "vue-property-decorator";
import adminService from "@/services/AdminService";
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import { organisationModule } from "@/store/modules/OrganisationModule";
import {
  IAirport,
  IAirportAlias,
  IAirportAliasReview,
  IAirportCreateRequest,
} from "@/models/Airport";

@Component
export default class AirportAliasReview extends Vue {
  public loading = false;

  public airports: IAirport[] = [];
  public selectedAirport: IAirport = null;

  public reviews: IAirportAliasReview[] = [];
  public selectedReviews: IAirportAliasReview[] = [];

  async mounted() {
    await this.getAirports();
    await this.getReviews();
  }

  // Airport methods
  public async getAirports() {
    this.airports = await adminService.getAirports();
  }

  get sortedAirports() {
    return [...this.airports].sort((a, b) => a.name.localeCompare(b.name));
  }

  public selectAirport(airport: IAirport) {
    this.selectedReviews = [];

    if (this.selectedAirport && this.selectedAirport.id === airport.id) {
      this.selectedAirport = null;
    } else {
      this.selectedAirport = airport;
      this.selectedReviews = this.reviews.filter((review) =>
        airport.aliases.some((alias) => alias.id === review.airportAliasId)
      );
    }
  }

  get isAirportSelected() {
    return (airport: IAirport) =>
      this.selectedAirport && this.selectedAirport.id === airport.id;
  }

  // Review methods
  public async getReviews() {
    this.reviews = await adminService.getAirportAliasReviews();
  }

  get sortedReviews() {
    return [...this.reviews].sort((a, b) =>
      a.airportAlias.localeCompare(b.airportAlias)
    );
  }

  public selectReview(review: IAirportAliasReview) {
    if (this.selectedReviews.some((x) => x.id === review.id)) {
      this.selectedReviews = this.selectedReviews.filter(
        (x) => x.id !== review.id
      );
    } else {
      this.selectedReviews.push(review);
    }
  }

  get isReviewSelected() {
    return (review: IAirportAliasReview) =>
      this.selectedReviews.some((x) => x.id === review.id);
  }

  // Utils
  get isSaveDisabled(): boolean {
    if (this.loading || !this.selectedAirport) {
      return true;
    }

    const aliasIds = this.selectedAirport.aliases
      .map((alias) => alias.id)
      .sort();

    const aliasReviewIds = this.selectedReviews
      .map((review) => review.airportAliasId)
      .sort();

    // return false (not disabled) if the lengths don't match
    if (aliasIds.length !== aliasReviewIds.length) {
      return false;
    }

    // return false (not disabled) if any of the ids don't match
    for (let i = 0; i < aliasIds.length; i++) {
      if (aliasIds[i] !== aliasReviewIds[i]) {
        return false;
      }
    }

    // return true (disabled) if all ids match
    return true;
  }

  get badges() {
    const badges = new Map();

    for (const review of this.reviews) {
      let badgeArray = [];

      if (!review.completed) badgeArray.push({ text: "Ny", variant: "danger" });

      // Add a badge with the airport info if the review's airport is not the selected airport
      const airport = this.airports.find((airport) =>
        airport.aliases.some((alias) => alias.id === review.airportAliasId)
      );
      if (
        airport &&
        (!this.selectedAirport || this.selectedAirport.id !== airport.id)
      ) {
        badgeArray.push({ text: airport.name, variant: "info" });
      }

      badgeArray = badgeArray.map((badge, index) => {
        const additionalClass =
          (!index ? "ml-1 " : "") +
          (badgeArray.length !== index + 1 ? "mr-1" : "");
        return { ...badge, class: additionalClass };
      });

      badges.set(review.id, badgeArray);
    }

    return badges;
  }

  // Alias CRUD
  public async updateAliases() {
    const aliasesToAdd: IAirportAlias[] = [];
    const aliasesToRemove: IAirportAlias[] = [];

    // Check if the selected airport has aliases that are not present in the selected reviews
    for (const alias of this.selectedAirport.aliases) {
      const foundReview = this.selectedReviews.find(
        (review) => review.airportAliasId === alias.id
      );

      if (!foundReview) {
        aliasesToRemove.push(alias);
      }
    }

    // Check if the selected reviews have aliases that are not present in the selected airport
    for (const review of this.selectedReviews) {
      const foundAlias = this.selectedAirport.aliases.find(
        (alias) => alias.id === review.airportAliasId
      );
      if (!foundAlias) {
        aliasesToAdd.push({
          id: review.airportAliasId,
          textValue: review.airportAlias,
        });
      }
    }

    const addAliasPromises = aliasesToAdd.map((aliasToAdd) =>
      adminService.addAirportAlias(this.selectedAirport.id, aliasToAdd.id)
    );

    const removeAliasPromises = aliasesToRemove.map((aliasToRemove) =>
      adminService.deleteAirportAlias(this.selectedAirport.id, aliasToRemove.id)
    );

    this.loading = true;

    await Promise.all([...addAliasPromises, ...removeAliasPromises]);

    await this.getAirports();
    await this.getReviews();

    this.loading = false;
  }

  // Airport CRUD
  public async deleteAirport(airport: IAirport) {
    const msg =
      "Är du säker? Om du tar bort flygplatsen kommer alla sparade flygresor från eller till denna flygplats tas bort.";
    if (!confirm(msg)) return;

    if (!(await adminService.deleteAirport(airport.id))) return;

    if (this.selectedAirport && this.selectedAirport.id === airport.id) {
      this.selectedAirport = null;
      this.selectedReviews = [];
    }

    await this.getAirports();
    await this.getReviews();
  }

  public showCreateAirportModal: boolean = false;

  public showUpdateAirportModal: boolean = false;
  public airportIdToUpdate: string = "";

  public airportForm: IAirportCreateRequest = {
    name: "",
    iataCode: "",
    icaoCode: "",
  };

  public airportFormSubmitted: boolean = false;
  public airportFormError: boolean = false;
  public airportFormErrorMsg: string = "";

  get nameState(): boolean {
    if (!this.airportFormSubmitted) return null;

    this.airportFormError = false;

    return this.airportForm.name.length > 0;
  }

  get iataCodeState(): boolean {
    if (!this.airportFormSubmitted) return null;

    this.airportFormError = false;

    return (
      this.airportForm.iataCode.length === 3 &&
      /^[A-Z]+$/.test(this.airportForm.iataCode)
    );
  }

  get icaoCodeState(): boolean {
    if (!this.airportFormSubmitted) return null;

    this.airportFormError = false;

    return (
      this.airportForm.icaoCode.length === 4 &&
      /^[A-Z]+$/.test(this.airportForm.icaoCode)
    );
  }

  public async openCreateAirportModal() {
    this.airportForm = {
      name: "",
      iataCode: "",
      icaoCode: "",
    };

    this.showCreateAirportModal = true;
  }

  public async submitNewAirportForm() {
    this.airportFormSubmitted = true;
    this.airportFormError = false;

    if (this.nameState && this.iataCodeState && this.icaoCodeState) {
      try {
        await adminService.addAirport(this.airportForm);

        this.showCreateAirportModal = false;

        this.airportForm = {
          name: "",
          iataCode: "",
          icaoCode: "",
        };

        this.airportFormSubmitted = false;
        await this.getAirports();
      } catch (error) {
        this.airportFormError = true;
        this.airportFormErrorMsg = error.response && error.response.data;
      }
    }
  }

  public async openUpdateAirportModal(airport: IAirport) {
    this.airportForm = {
      name: airport.name,
      iataCode: airport.iataCode,
      icaoCode: airport.icaoCode,
    };

    this.airportIdToUpdate = airport.id;

    this.showUpdateAirportModal = true;
  }

  public async submitUpdateAirportForm() {
    this.airportFormSubmitted = true;
    this.airportFormError = false;

    if (this.nameState && this.iataCodeState && this.icaoCodeState) {
      try {
        await adminService.updateAirport(
          this.airportIdToUpdate,
          this.airportForm
        );

        this.showUpdateAirportModal = false;

        this.airportForm = {
          name: "",
          iataCode: "",
          icaoCode: "",
        };

        this.airportFormSubmitted = false;
        await this.getAirports();
      } catch (error) {
        this.airportFormError = true;
        this.airportFormErrorMsg = error.response && error.response.data;
      }
    }
  }
}
